import {
  Code2, Globe, Smartphone, BrainCircuit, ScanLine, Gamepad2,
  Boxes, Cloud, Plug, Building2, Wrench, Palette,
} from "lucide-react";

export const SERVICES = [
  {
    slug: "software-development",
    icon: Code2,
    title: "Software Development",
    short: "End-to-end engineering for products that scale from MVP to millions.",
    long:
      "We architect, build and operate production software systems — from polyglot microservices to event-driven platforms — engineered for reliability, performance and long-term maintainability.",
    keywords: "software development, custom engineering, microservices, scalable software",
    benefits: [
      { title: "Faster Time to Market", desc: "Shipping production-ready releases in weeks, not quarters, with battle-tested delivery playbooks." },
      { title: "Engineering at Scale", desc: "Architectures validated for millions of users, with observability baked in from day one." },
      { title: "Predictable Delivery", desc: "Fixed-scope sprints, transparent burn-downs and zero-surprise rollouts." },
      { title: "Lower Total Cost", desc: "Right-sized infrastructure, automated testing and reusable component libraries cut long-term spend." },
    ],
    features: [
      "Greenfield product engineering",
      "Legacy system modernization",
      "API platforms & integrations",
      "Microservices & event-driven systems",
      "DevOps & CI/CD pipelines",
      "Performance & load engineering",
      "Security & compliance hardening",
      "24/7 SRE & observability",
    ],
    stack: ["React", "Next.js", "Node.js", "Python", "Go", "Java", "PostgreSQL", "MongoDB", "Redis", "Kafka", "AWS", "GCP", "Docker", "Kubernetes"],
    industries: ["FinTech", "Healthcare", "Logistics", "Retail", "SaaS", "EdTech"],
    faqs: [
      { q: "How do you estimate timelines?", a: "Every engagement starts with a 1-2 week discovery sprint where we map scope, dependencies and risk. We then commit to a fixed timeline with weekly checkpoints." },
      { q: "Do you work with our existing engineering team?", a: "Yes. We blend seamlessly into your processes — Git, Jira, Slack, your CI — and pair-program with your engineers when needed." },
      { q: "What about IP and code ownership?", a: "All code, designs and assets we produce are 100% your IP from day one." },
      { q: "Can you sign NDAs and MSAs?", a: "Absolutely. We work under enterprise NDAs, MSAs and SOC2-aligned data handling." },
    ],
  },
  {
    slug: "web-development",
    icon: Globe,
    title: "Web Development",
    short: "Fast, accessible, SEO-perfect websites and web applications.",
    long:
      "From marketing sites that convert to complex B2B SaaS dashboards, we build web experiences that are blazing fast, beautifully designed and engineered for Core Web Vitals.",
    keywords: "web development, react, nextjs, web applications, saas",
    benefits: [
      { title: "Sub-second Loads", desc: "Edge-rendered, image-optimized and code-split for instant interactivity globally." },
      { title: "Conversion-First Design", desc: "Every layout, CTA and micro-interaction is tuned to move the metric that matters." },
      { title: "Built for SEO", desc: "Semantic HTML, structured data, sitemaps and content velocity workflows out of the box." },
      { title: "Accessible by Default", desc: "WCAG 2.2 AA compliance, keyboard nav and screen-reader support without compromise." },
    ],
    features: [
      "Marketing & landing sites",
      "B2B SaaS dashboards",
      "Headless commerce storefronts",
      "Portals & internal tools",
      "Progressive Web Apps",
      "CMS integrations (Sanity, Contentful, Strapi)",
      "Analytics & experimentation",
      "Core Web Vitals optimization",
    ],
    stack: ["React", "Next.js", "Remix", "Astro", "Tailwind", "TypeScript", "Vercel", "Cloudflare", "Sanity", "Contentful"],
    industries: ["SaaS", "E-commerce", "Media", "EdTech", "FinTech", "Real Estate"],
    faqs: [
      { q: "Do you build with WordPress?", a: "Only on request. We default to modern headless stacks (Next.js + Sanity/Contentful) for performance, security and developer happiness." },
      { q: "Can you migrate our existing site?", a: "Yes — we routinely migrate WordPress, Webflow and bespoke stacks to Next.js with 301 redirects, SEO continuity and zero downtime." },
      { q: "How do you handle CMS for non-technical teams?", a: "We ship visual editors with structured content models so marketing teams can publish without engineering involvement." },
      { q: "What hosting do you recommend?", a: "Vercel, Cloudflare Pages or AWS Amplify depending on traffic patterns, edge needs and compliance posture." },
    ],
  },
  {
    slug: "mobile-app-development",
    icon: Smartphone,
    title: "Mobile App Development",
    short: "Native-quality iOS and Android apps from a single codebase.",
    long:
      "We design and ship cross-platform mobile apps that feel native, run smoothly on low-end devices and ship updates over-the-air — all backed by enterprise security.",
    keywords: "mobile app development, ios, android, react native, flutter",
    benefits: [
      { title: "One Codebase, Two Stores", desc: "React Native and Flutter deliver native performance with a single engineering investment." },
      { title: "App Store Ready", desc: "From provisioning profiles to App Store screenshots — we ship and we get approved." },
      { title: "Offline-First UX", desc: "Local persistence, conflict resolution and resilient sync for users on flaky networks." },
      { title: "Push, Payments, AI", desc: "Notifications, in-app purchases and on-device AI inferences shipped end to end." },
    ],
    features: [
      "iOS native (Swift / SwiftUI)",
      "Android native (Kotlin / Jetpack Compose)",
      "Cross-platform (React Native, Flutter)",
      "Wearable apps (watchOS, Wear OS)",
      "In-app purchases & subscriptions",
      "Push notifications & deep linking",
      "App Store & Play submission",
      "Crash analytics & performance monitoring",
    ],
    stack: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "RevenueCat", "Sentry", "OneSignal", "GraphQL"],
    industries: ["FinTech", "Healthcare", "E-commerce", "Travel", "Education", "Logistics"],
    faqs: [
      { q: "Native or cross-platform?", a: "We start with React Native or Flutter unless deep platform APIs (ARKit, CoreML, complex audio) force native. We'll advise based on your specific feature roadmap." },
      { q: "How long does store approval take?", a: "Apple typically reviews within 24-48 hours; Google within hours. We handle the entire submission, screenshots and metadata." },
      { q: "Do you support older devices?", a: "Yes. We target iOS 15+ and Android API 24+ by default, with graceful degradation strategies for older OS versions." },
      { q: "Can you take over an existing app?", a: "Frequently. We audit, refactor incrementally and pay down debt while shipping new features." },
    ],
  },
  {
    slug: "ai-services",
    icon: BrainCircuit,
    title: "AI Services",
    short: "Production AI: from LLM apps to computer vision pipelines.",
    long:
      "We help organizations move beyond AI experiments to deployed, monitored, ROI-positive AI products — RAG systems, agents, multimodal pipelines and on-device inference.",
    keywords: "ai services, machine learning, llm integration, generative ai, computer vision",
    benefits: [
      { title: "From PoC to Production", desc: "Most AI projects die at the demo. Ours ship — with eval suites, guardrails and observability." },
      { title: "Vendor-Neutral", desc: "We architect for OpenAI, Anthropic, Gemini, open-source models and on-prem — switching is a config change, not a rewrite." },
      { title: "Cost-Aware", desc: "Model routing, caching and quantization cut LLM bills by 40-70% without quality regressions." },
      { title: "Safe & Compliant", desc: "PII redaction, prompt injection defense, output filtering and full audit trails for regulated industries." },
    ],
    features: [
      "LLM application development",
      "Retrieval-Augmented Generation (RAG)",
      "Multi-agent systems",
      "Generative AI products",
      "Computer Vision pipelines",
      "Natural Language Processing",
      "Conversational AI & voice",
      "Prompt engineering & evals",
      "AI consulting & strategy",
      "Machine learning operations (MLOps)",
    ],
    stack: ["OpenAI GPT", "Anthropic Claude", "Google Gemini", "LangChain", "LlamaIndex", "Pinecone", "Weaviate", "Hugging Face", "PyTorch", "TensorFlow", "Ray", "MLflow"],
    industries: ["Healthcare", "FinTech", "Legal", "Retail", "Manufacturing", "Media"],
    faqs: [
      { q: "Which model should we use?", a: "It depends on latency, cost, accuracy and data residency. Our discovery quantifies the trade-offs across 4-6 candidates with your real data." },
      { q: "Can we run AI on-premise?", a: "Yes. We deploy open-weight models (Llama, Mistral, Qwen) on your infrastructure with full ops support." },
      { q: "How do you handle hallucinations?", a: "Layered defense: grounded RAG, structured outputs, programmatic evals, human-in-the-loop and confidence-based fallbacks." },
      { q: "Do you fine-tune models?", a: "When the data justifies it. Often, better prompting + RAG outperforms fine-tuning at a fraction of the cost — we'll advise honestly." },
    ],
  },
  {
    slug: "data-annotation",
    icon: ScanLine,
    title: "Data Annotation",
    short: "High-quality datasets for training, evaluating and aligning AI models.",
    long:
      "Enterprise-scale annotation across image, text, audio, video and 3D — with rigorous QA, human-in-the-loop workflows and compliance for medical and autonomous-vehicle data.",
    keywords: "data annotation, dataset labeling, computer vision annotation, nlp annotation",
    benefits: [
      { title: "Trained Annotators", desc: "Domain-specialist teams for medical, legal, automotive and multilingual workloads." },
      { title: "Multi-Tier QA", desc: "Consensus labeling, golden sets and statistical sampling deliver 98%+ agreement scores." },
      { title: "Secure by Design", desc: "ISO 27001-aligned facilities, NDA-bound annotators and HIPAA workflows for PHI." },
      { title: "Scalable Throughput", desc: "From 10K to 10M labels per month with elastic capacity and SLA-backed delivery." },
    ],
    features: [
      "Image annotation (bounding box, polygon, keypoint)",
      "Semantic & instance segmentation",
      "OCR & document annotation",
      "Text classification & NER",
      "Sentiment & intent labeling",
      "Audio transcription & diarization",
      "Video frame & temporal labeling",
      "Medical imaging (DICOM)",
      "Autonomous vehicle (LiDAR, 3D cuboid)",
      "RLHF & preference ranking",
      "Human-in-the-loop pipelines",
      "Multi-tier quality assurance",
    ],
    stack: ["CVAT", "Label Studio", "Labelbox", "SuperAnnotate", "Custom in-house tooling", "AWS S3", "Snowflake"],
    industries: ["Autonomous Vehicles", "Healthcare", "Retail", "Agritech", "Security", "Media"],
    faqs: [
      { q: "What is your minimum project size?", a: "We take projects from 5K labels (specialist domains) to 10M+ labels (high-volume CV). Pricing scales with volume and complexity." },
      { q: "How do you ensure quality?", a: "Triple-annotation on samples, golden datasets, IoU/F1 tracking, annotator scorecards and weekly calibration sessions." },
      { q: "Can you handle sensitive data?", a: "Yes. We offer on-premise labeling, air-gapped workstations and HIPAA / GDPR compliant workflows." },
      { q: "Do you provide the labeling platform?", a: "Either — we work in your platform or provide our own. We're tool-agnostic." },
    ],
  },
  {
    slug: "game-development",
    icon: Gamepad2,
    title: "Game Development",
    short: "Mobile, casual and educational games engineered for retention.",
    long:
      "We design and ship games across Unity, Unreal and HTML5 — with live-ops, leaderboards and multiplayer infrastructure built for global scale.",
    keywords: "game development, unity, unreal, mobile games, multiplayer",
    benefits: [
      { title: "Retention-Tuned", desc: "Core loops, progression and economies designed against benchmarked D1/D7/D30 targets." },
      { title: "Live Operations", desc: "Events, A/B tests and remote config to keep the game fresh long after launch." },
      { title: "Cross-Platform", desc: "Ship once to iOS, Android, Web and Steam with shared codebases and asset pipelines." },
      { title: "Multiplayer Ready", desc: "Authoritative servers, matchmaking and netcode tuned for sub-100ms experiences." },
    ],
    features: [
      "Unity 2D & 3D games",
      "Unreal Engine experiences",
      "HTML5 / WebGL games",
      "Mobile casual & hyper-casual",
      "Educational games & gamification",
      "Multiplayer & matchmaking",
      "Leaderboards & social systems",
      "In-game economy design",
      "Live-ops & event tooling",
      "Backend APIs & cloud saves",
      "Analytics & monetization",
    ],
    stack: ["Unity", "Unreal Engine", "Phaser", "PlayCanvas", "PlayFab", "Firebase", "AWS GameLift", "Photon"],
    industries: ["Entertainment", "Education", "Advertising", "Health & Wellness", "Training"],
    faqs: [
      { q: "Do you publish games?", a: "We can co-publish or hand the build to your publisher. We handle all store assets, age ratings and submission." },
      { q: "Unity or Unreal?", a: "Unity for mobile, casual and 2D. Unreal for high-fidelity 3D and console. We'll recommend based on your art direction and target devices." },
      { q: "How do you monetize?", a: "IAP, rewarded ads, battle passes, subscriptions — we model LTV and choose the mix that fits your audience and platform policies." },
      { q: "Can you run live-ops post-launch?", a: "Yes. We offer ongoing live-ops teams that run events, balance economies and ship content cadences." },
    ],
  },
  {
    slug: "custom-software",
    icon: Boxes,
    title: "Custom Software Development",
    short: "ERP, CRM and industry platforms tailored to your operating model.",
    long:
      "When off-the-shelf software bends your processes the wrong way, we build the platform that fits — ERP, CRM, inventory, billing and vertical SaaS.",
    keywords: "custom software development, erp, crm, enterprise platforms",
    benefits: [
      { title: "Fits Your Process", desc: "Software that mirrors how your business actually runs — not the other way around." },
      { title: "Own Your Roadmap", desc: "No vendor lock-in, no surprise license hikes, no waiting on someone else's release schedule." },
      { title: "Connects Everything", desc: "Deep integrations with your existing accounting, HR, logistics and BI stack." },
      { title: "Future-Proof", desc: "Modular architectures and clean APIs so you can extend and replace components as you grow." },
    ],
    features: [
      "ERP platforms",
      "CRM systems",
      "Inventory & warehouse management",
      "Billing & subscription engines",
      "Healthcare platforms (EHR, telehealth)",
      "EdTech learning platforms",
      "FinTech back-office & ledger",
      "Travel & booking engines",
      "Manufacturing MES & OEE",
      "Logistics & fleet management",
      "Enterprise data platforms",
      "Workflow & approval engines",
    ],
    stack: ["React", "Node.js", "Python", "Django", "Laravel", "Java Spring", ".NET", "PostgreSQL", "MongoDB", "Redis", "RabbitMQ", "AWS", "Azure"],
    industries: ["Manufacturing", "Healthcare", "Logistics", "Retail", "Education", "Real Estate", "Travel", "FinTech"],
    faqs: [
      { q: "Should we build or buy?", a: "We'll tell you honestly. If a SaaS exists that covers 80%, we'll recommend it. We build when off-the-shelf forces compromises that hurt your business." },
      { q: "How do you handle data migration?", a: "Phased migration with parallel runs, reconciliation reports and rollback windows. We've migrated terabyte-scale legacy systems with zero data loss." },
      { q: "What about training and adoption?", a: "We deliver in-app onboarding, video libraries, admin documentation and on-site change-management workshops." },
      { q: "Can you maintain it long-term?", a: "Yes. Most clients retain us on an SLA-backed support contract covering ops, security patches and roadmap delivery." },
    ],
  },
];

export const EXTENDED_SERVICES = [
  { icon: Cloud, title: "Cloud Solutions", short: "Cloud-native architectures on AWS, Azure and GCP with cost-aware FinOps." },
  { icon: Plug, title: "API Development", short: "REST and GraphQL APIs designed for composition, scale and developer joy." },
  { icon: Building2, title: "Enterprise Solutions", short: "Workflows, integrations and platforms for Fortune 500 operating models." },
  { icon: Wrench, title: "Maintenance & Support", short: "24/7 SRE, security patching and continuous improvement under SLA." },
  { icon: Palette, title: "UI/UX Design", short: "Research-led product design, design systems and interaction craft." },
];

export const SERVICE_BY_SLUG = Object.fromEntries(SERVICES.map((s) => [s.slug, s]));
